import { useEffect, useRef } from 'react';

import type { Toast } from 'primereact/toast';
import type { UseFormSetValue, UseFormWatch } from 'react-hook-form';

type Options = {
  toast?: React.RefObject<Toast>;
};

export function useAutosyncCounts(
  watch: UseFormWatch<any>,
  setValue: UseFormSetValue<any>,
  opts?: Options,
) {
  const gradeABC = Number(watch('gradeABC') ?? 0);
  const gradeD = Number(watch('gradeD') ?? 0);
  const gradeE = Number(watch('gradeE') ?? 0);
  const dead = Number(watch('dead') ?? 0);
  const comments = String(watch('comments') ?? '');

  const lastAutoSplitRef = useRef<string | null>(null);

  useEffect(() => {
    const total = gradeABC + gradeD + gradeE;
    const numInMat = total - dead;

    setValue('total', total, { shouldValidate: false, shouldDirty: false });
    setValue('numInMat', numInMat, {
      shouldValidate: false,
      shouldDirty: false,
    });

    const deadLine = `${dead} Dead`;

    const isSplitLike = (l: string) => /^split\s*-\s*/i.test(l.trim());
    const isDeadLine = (l: string) => /^\d+\s*de[a-z]*\b/i.test(l.trim());

    const normalize = (s: string) =>
      s
        .trim()
        .replace(/\s*\/\s*/g, '/')
        .replace(/\s+/g, ' ')
        .toLowerCase();

    const makeAutoSplit = (count: number): string => {
      const STRAW_CAP = 39;
      const straws = Math.ceil(count / STRAW_CAP);
      const base = Math.floor(count / straws);
      const remainder = count % straws;
      const parts = Array.from(
        { length: straws },
        (_, i) => base + (i < remainder ? 1 : 0),
      );
      return `Split - ${parts.join('/')}`;
    };

    const parseSplitParts = (line: string): number[] | null => {
      const m = line.match(/^split\s*-\s*(\d+(?:\s*\/\s*\d+)*)\s*$/i);
      if (!m) return null;
      return m[1]
        .split('/')
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
        .map((n) => Number(n));
    };

    const lines = comments.split('\n');

    let rawExistingSplitLine: string | null = null;
    const baseLines: string[] = [];
    for (const l of lines) {
      const trimmed = l.trim();
      if (isSplitLike(trimmed)) {
        if (!rawExistingSplitLine) rawExistingSplitLine = l;
        continue;
      }
      if (isDeadLine(trimmed)) continue;
      baseLines.push(l);
    }

    const nextLines: string[] = [...baseLines];

    if (dead > 0) nextLines.push(deadLine);

    if (numInMat > 39) {
      const autoSplit = makeAutoSplit(numInMat);
      const normalizedAuto = normalize(autoSplit);

      let keepUserEdited = false;
      let lineToKeep = rawExistingSplitLine ?? null;

      if (rawExistingSplitLine) {
        const normalizedExisting = normalize(rawExistingSplitLine);

        const normalizedLastAuto = lastAutoSplitRef.current
          ? normalize(lastAutoSplitRef.current)
          : null;
        const isAutoManaged =
          normalizedExisting === normalizedAuto ||
          (normalizedLastAuto && normalizedExisting === normalizedLastAuto);

        if (isAutoManaged) {
          nextLines.push(autoSplit);
          lastAutoSplitRef.current = autoSplit;
          lineToKeep = autoSplit;
        } else {
          keepUserEdited = true;
          nextLines.push(rawExistingSplitLine);
        }
      } else {
        nextLines.push(autoSplit);
        lastAutoSplitRef.current = autoSplit;
        lineToKeep = autoSplit;
      }
      const lineForValidation = keepUserEdited
        ? rawExistingSplitLine
        : lineToKeep;
      if (lineForValidation) {
        const parts = parseSplitParts(lineForValidation);
        if (parts) {
          const valid = parts.every((n) => Number.isFinite(n) && n >= 0);
          const sum = parts.reduce((a, b) => a + b, 0);
          if (valid && sum !== numInMat) {
            opts?.toast?.current?.show({
              severity: 'error',
              summary: 'Split total mismatch',
              detail: `Split ${parts.join('/')} ≠ # In Mat ${numInMat}. Adjust the numbers.`,
              life: 4000,
            });
          }
        }
      }
    } else {
      lastAutoSplitRef.current = null;
    }

    const rebuilt = nextLines.join('\n');
    if (rebuilt !== comments) {
      setValue('comments', rebuilt, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }
  }, [gradeABC, gradeD, gradeE, dead, comments, setValue, opts?.toast]);
}
